import React, { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { parseObservacaoAuditEntries } from "../../utils/clientObservacao";

function AuditIcon({ type }) {
  if (type === "merge") return <MergeTypeIcon fontSize="small" color="info" />;
  if (type === "hygiene") return <AutoFixHighIcon fontSize="small" color="action" />;
  return <HistoryIcon fontSize="small" color="action" />;
}

/**
 * Notas internas editáveis + histórico automático (unificação / higienização) somente leitura.
 */
export default function ClientObservacaoField({
  value = "",
  onChange,
  auditLines = [],
  error,
  helperText,
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const entries = useMemo(() => parseObservacaoAuditEntries(auditLines), [auditLines]);
  const hasHistory = entries.length > 0;

  return (
    <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
      <TextField
        label="Notas internas"
        name="observacao"
        value={value}
        onChange={onChange}
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        size="small"
        placeholder="Anotações visíveis apenas para a equipe (atendimento, contexto comercial, etc.)"
        error={!!error}
        helperText={
          error ||
          helperText ||
          "Não inclua aqui registros de sistema — unificações e higienizações são registradas automaticamente."
        }
      />

      {hasHistory ? (
        <Box
          sx={{
            mt: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.5,
              py: 1,
              cursor: "pointer",
              bgcolor: (t) => alpha(t.palette.action.hover, 0.04),
            }}
            onClick={() => setHistoryOpen((o) => !o)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={600}>
                Registro automático
              </Typography>
              <Chip size="small" label={entries.length} sx={{ height: 20, fontSize: "0.7rem" }} />
            </Stack>
            <IconButton
              size="small"
              aria-label={historyOpen ? "Recolher histórico" : "Expandir histórico"}
              sx={{
                transform: historyOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s ease",
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Collapse in={historyOpen}>
            <Stack spacing={1.25} sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Gerado pelo Xpro ao unificar cadastros ou aplicar higienização. Não editável — consulte
                também a aba Linha do tempo no perfil do cliente.
              </Typography>
              {entries.map((entry, idx) => (
                <Stack
                  key={`${entry.type}-${entry.date}-${idx}`}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    px: 1.25,
                    borderRadius: 1.5,
                    bgcolor: (t) => alpha(t.palette.background.default, 0.6),
                  }}
                >
                  <Box sx={{ pt: 0.25, flexShrink: 0 }}>
                    <AuditIcon type={entry.type} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                      {entry.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {[entry.subtitle, entry.date].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}
